import {
  selectSplitScreenSlice,
  toggleSplitModulePicker,
  toggleSplitScreen,
  type ISplitScreenSlice,
  setHighlightColor,
  setSplitPath,
  setActivePanel,
  stopSplitScreen,
  setIsSplitScreenAvailable,
  startSplitScreen,
  setIsSplitScreenOpen,
} from "@/store/slices/split-screen.slice";
import React, { createContext, useContext, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { ActivePanelTypes } from "./split-screen.constants";
import { useMediaQuery } from "usehooks-ts";

type SplitScreenContextType = ISplitScreenSlice & {
  setSplitPath: (path: string | null) => void;
  toggleSplitScreen: () => void;
  toggleModulePicker: () => void;
  setHighlightColor: (color: string) => void;
  setActivePanel: (panel: ActivePanelTypes) => void;
  startSplitScreen: (pageUrl?: string) => void;
  stopSplitScreen: () => void;
  setIsSplitScreenAvailable: (available: boolean) => void;
  splitPortalRef: React.RefObject<HTMLDivElement | null>;
};

const SplitScreenContext = createContext<SplitScreenContextType | undefined>(undefined);

export function SplitScreenProvider({ children }: { children: React.ReactNode }) {
  const {
    isModulePickerOpen,
    showSplitScreen,
    highlightColor,
    isSplitScreenClosed,
    splitPath,
    activePanel,
    isSplitScreenAvailable,
  } = useSelector(selectSplitScreenSlice);
  const dispatch = useDispatch();

  // TO ENABLED IF NEED TO HIDE SPLIT SCREEN ON MOBILE
  const isXlScreen = useMediaQuery("(min-width: 1200px)");
  useLayoutEffect(() => {
    if (!isSplitScreenAvailable) dispatch(setIsSplitScreenOpen(true));

    if (!isXlScreen) dispatch(setIsSplitScreenAvailable(false));
    else if (isXlScreen) dispatch(setIsSplitScreenAvailable(true));
  }, [isXlScreen, setIsSplitScreenAvailable]);

  const splitPortalRef = useRef<HTMLDivElement | null>(null);

  return (
    <SplitScreenContext.Provider
      value={{
        splitPath,
        activePanel,
        highlightColor,
        splitPortalRef,
        showSplitScreen,
        isModulePickerOpen,
        isSplitScreenClosed,
        isSplitScreenAvailable,
        startSplitScreen: (pageUrl: string | undefined) => dispatch(startSplitScreen(pageUrl)),
        stopSplitScreen: () => dispatch(stopSplitScreen()),
        toggleSplitScreen: () => dispatch(toggleSplitScreen()),
        setActivePanel: (panel) => dispatch(setActivePanel(panel)),
        toggleModulePicker: () => dispatch(toggleSplitModulePicker()),
        setSplitPath: (path: string | null) => dispatch(setSplitPath(path)),
        setHighlightColor: (color: string) => dispatch(setHighlightColor(color)),
        setIsSplitScreenAvailable: (available: boolean) => dispatch(setIsSplitScreenAvailable(available)),
      }}
    >
      {children}
    </SplitScreenContext.Provider>
  );
}

// eslint-disable-next-line
export function useSplitScreenContext() {
  const context = useContext(SplitScreenContext);
  if (context === undefined) {
    throw new Error("useSplitScreen must be used within a SplitScreenProvider");
  }
  return context;
}
